import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Users,
  UserPlus,
  Trash2,
  Crown,
  Shield,
  Eye,
  Loader2,
  Mail,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const ROLE_BADGES = {
  owner: { label: "Owner", color: "bg-amber-500/10 text-amber-600 dark:text-amber-400", icon: Crown },
  operator: { label: "Operator", color: "bg-blue-500/10 text-blue-600 dark:text-blue-400", icon: Shield },
  member: { label: "Member", color: "bg-slate-500/10 text-slate-600 dark:text-slate-400", icon: Eye },
};

const ROLE_DESCRIPTIONS = {
  owner: "Full access: can invite/remove members, modify settings, delete environment",
  operator: "Can modify settings, trigger scans, manage credentials",
  member: "Read-only access to dashboards and reports",
};

/**
 * Team Members Component
 * 
 * Displays and manages team members for an environment.
 * - Lists current members with their roles
 * - Allows inviting new members (owner/operator only)
 * - Allows removing members (owner/operator only)
 * - Allows changing member roles (owner only)
 */
export default function TeamMembers({ api, environmentId, environmentName, isOwner, isOperator }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const canManageMembers = isOwner || isOperator;

  const fetchMembers = useCallback(async () => {
    if (!environmentId) return;
    try {
      const res = await api().get(`/team/environments/${environmentId}/members`);
      setMembers(res.data.members || []);
    } catch (err) {
      console.error("Failed to load team members:", err);
      // Don't show toast on initial load failure - might be expected for new environments
    } finally {
      setLoading(false);
    }
  }, [api, environmentId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setInviting(true);
    try {
      await api().post(`/team/environments/${environmentId}/members`, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      setInviteRole("member");
      setShowInvite(false);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`Remove ${member.user_email || member.user_id} from ${environmentName}? They will lose access immediately.`)) {
      return;
    }

    setRemovingId(member.user_id);
    try {
      await api().delete(`/team/environments/${environmentId}/members/${member.user_id}`);
      toast.success("Member removed");
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove member");
    } finally {
      setRemovingId(null);
    }
  };

  const handleRoleChange = async (member, newRole) => {
    try {
      await api().put(`/team/environments/${environmentId}/members/${member.user_id}`, {
        role: newRole,
      });
      toast.success(`Updated ${member.user_email || member.user_id}'s role to ${newRole}`);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-4 h-4 animate-spin text-[var(--hig-accent)]" />
        <span className="ml-2 text-xs text-[var(--n-text-muted)]">Loading team...</span>
      </div>
    );
  }

  return (
    <div data-testid={`team-members-${environmentId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-3.5 h-3.5 text-[var(--hig-accent)]" />
          <span className="text-xs font-medium">Team Members</span>
          <Badge variant="outline" className="text-[8px] px-1 py-0">{members.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchMembers}
            className="text-[10px] h-7 px-2"
            data-testid={`refresh-team-${environmentId}`}
          >
            <RefreshCw className="w-3 h-3" />
          </Button>
          {canManageMembers && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowInvite(true)}
              className="text-[10px] h-7 px-2"
              data-testid={`invite-member-${environmentId}`}
            >
              <UserPlus className="w-3 h-3 mr-1" /> Invite
            </Button>
          )}
        </div>
      </div>

      {members.length === 0 ? (
        <p className="text-[10px] text-[var(--n-text-muted)] italic py-2">
          No team members yet. You are the only one with access.
        </p>
      ) : (
        <div className="space-y-1.5">
          {members.map((member) => {
            const roleInfo = ROLE_BADGES[member.role] || ROLE_BADGES.member;
            const RoleIcon = roleInfo.icon;
            const isOwnerMember = member.role === "owner";
            const canRemove = canManageMembers && !isOwnerMember && (isOwner || member.role === "member");
            const canChangeRole = isOwner && !isOwnerMember;

            return (
              <div
                key={member.user_id || member.id}
                className="flex items-center justify-between p-2.5 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)]/50"
                data-testid={`team-member-${member.user_id || member.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center ${roleInfo.color.split(" ")[0]}`}>
                    <RoleIcon className={`w-3.5 h-3.5 ${roleInfo.color.split(" ").slice(1).join(" ")}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium truncate">
                        {member.user_name || member.user_email || member.user_id?.slice(0, 8)}
                      </span>
                      {member.status === "pending" && (
                        <Badge className="text-[8px] px-1 py-0 bg-amber-500/10 text-amber-500">
                          Pending
                        </Badge>
                      )}
                    </div>
                    {member.user_email && (
                      <span className="text-[10px] text-[var(--n-text-muted)] flex items-center gap-1">
                        <Mail className="w-2.5 h-2.5" /> {member.user_email}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {canChangeRole ? (
                    <Select
                      value={member.role}
                      onValueChange={(newRole) => handleRoleChange(member, newRole)}
                    >
                      <SelectTrigger className="h-7 w-24 text-[10px]" data-testid={`role-select-${member.user_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="operator" className="text-xs">
                          <div className="flex items-center gap-1.5">
                            <Shield className="w-3 h-3" /> Operator
                          </div>
                        </SelectItem>
                        <SelectItem value="member" className="text-xs">
                          <div className="flex items-center gap-1.5">
                            <Eye className="w-3 h-3" /> Member
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={`text-[9px] px-2 py-0.5 ${roleInfo.color}`}>
                      {roleInfo.label}
                    </Badge>
                  )}

                  {canRemove && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(member)}
                      disabled={removingId === member.user_id}
                      className="h-7 w-7 p-0 text-red-400 hover:text-red-500 hover:bg-red-500/10"
                      data-testid={`remove-member-${member.user_id}`}
                    >
                      {removingId === member.user_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="sm:max-w-md" data-testid="invite-member-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Invite Team Member</DialogTitle>
            <DialogDescription className="text-xs">
              Invite someone to access <strong>{environmentName}</strong>. They'll receive an email invitation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-xs font-medium mb-1.5 block">Email Address</label>
              <Input
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="h-9 text-sm"
                data-testid="invite-email-input"
              />
            </div>

            <div>
              <label className="text-xs font-medium mb-1.5 block">Role</label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="h-9" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isOwner && (
                    <SelectItem value="operator">
                      <div className="flex items-center gap-2">
                        <Shield className="w-3.5 h-3.5 text-blue-500" />
                        <div>
                          <span className="font-medium">Operator</span>
                          <p className="text-[10px] text-[var(--n-text-muted)]">Can modify settings, manage credentials</p>
                        </div>
                      </div>
                    </SelectItem>
                  )}
                  <SelectItem value="member">
                    <div className="flex items-center gap-2">
                      <Eye className="w-3.5 h-3.5 text-slate-500" />
                      <div>
                        <span className="font-medium">Member</span>
                        <p className="text-[10px] text-[var(--n-text-muted)]">Read-only access to dashboards</p>
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)] text-[10px] text-[var(--n-text-muted)]">
              <strong className="text-[var(--n-text-secondary)]">Note:</strong> The invited user must have a Niyant account. If they don't have one yet, ask them to sign up first.
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowInvite(false)}
              className="text-xs h-8"
            >
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
              className="bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent)]/90 text-xs h-8"
              data-testid="send-invite-btn"
            >
              {inviting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-3.5 h-3.5 mr-1.5" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
